<script lang="ts">
  import { onMount } from 'svelte';
  import type { Project, ProjectsByStatus, Scale } from '../lib/types';
  import {
    getWeekStart,
    getWeekEnd,
    getWeekDisplayRange,
    getWeekNumber,
    isCurrentWeek,
    isCurrentMonth,
    getShortMonthName,
    categoryIcons,
    statusAccentColors,
    parseCalendarDate,
    progressStyle,
    progressComplete,
  } from '../lib/utils';
  import { showProjectTooltip, hideProjectTooltip } from '../lib/stores/projectTooltip';
  import {
    draggedProject,
    openProjectRail,
    openProjectModalForCreate,
    requestProjectMove,
  } from '../lib/stores/ui';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    scale: Scale;
    // Week scale props
    visibleWeeks?: string[];
    projectsByWeek?: Record<string, ProjectsByStatus>;
    currentWeek?: string;
    // Month scale props
    visibleMonths?: string[];
    projectsByMonth?: Record<string, ProjectsByStatus>;
    currentMonth?: string;
    // Common
    range?: string;
  }

  let {
    scale,
    visibleWeeks = [],
    projectsByWeek = {},
    currentWeek = '',
    visibleMonths = [],
    projectsByMonth = {},
    currentMonth = '',
    range = 'quarter',
  }: Props = $props();

  // Determine if we're in week or month mode
  let isWeekScale = $derived(scale === 'week');

  // Unified column data
  interface ColumnData {
    key: string;
    label: string;
    sublabel: string;
    state: 'past' | 'current' | 'future';
  }

  let columns = $derived.by((): ColumnData[] => {
    if (isWeekScale) {
      return visibleWeeks.map((weekKey) => ({
        key: weekKey,
        label: getWeekDisplayRange(weekKey),
        sublabel: `W${getWeekNumber(weekKey)}`,
        state: getWeekState(weekKey),
      }));
    } else {
      return visibleMonths.map((monthKey) => ({
        key: monthKey,
        label: getShortMonthName(monthKey),
        sublabel: monthKey.split('-')[0], // year
        state: getMonthState(monthKey),
      }));
    }
  });

  // Current column index for highlighting
  let currentColumnIndex = $derived(
    isWeekScale ? visibleWeeks.indexOf(currentWeek) : visibleMonths.indexOf(currentMonth),
  );

  // Collect all unique projects with their date ranges
  interface GanttProject extends Project {
    ganttStartIndex: number;
    ganttEndIndex: number;
    ganttRow: number;
  }

  const STATUSES = ['active', 'planned', 'almost', 'done'] as const;

  // Get all unique projects across visible periods
  // Also builds a project→period index for fast fallback lookups
  let projectPeriodIndex: Map<string, number[]> | null = null;

  function getAllProjects(): Project[] {
    const seen = new Set<string>();
    const result: Project[] = [];
    const periodIndex = new Map<string, number[]>();

    const periodsData = isWeekScale ? projectsByWeek : projectsByMonth;
    const periods = isWeekScale ? visibleWeeks : visibleMonths;

    for (let i = 0; i < periods.length; i++) {
      const data = periodsData[periods[i]];
      if (!data) continue;

      for (const status of STATUSES) {
        const projects = data[status];
        if (!projects) continue;
        for (const project of projects) {
          // Track period index for fallback range calculation
          let indices = periodIndex.get(project.id);
          if (!indices) {
            indices = [];
            periodIndex.set(project.id, indices);
          }
          indices.push(i);

          if (!seen.has(project.id)) {
            seen.add(project.id);
            result.push(project);
          }
        }
      }
    }

    projectPeriodIndex = periodIndex;
    return result;
  }

  // Calculate which columns a project spans
  function getProjectColumnRange(project: Project): { startIndex: number; endIndex: number } {
    if (isWeekScale) {
      return getProjectWeekRange(project);
    } else {
      return getProjectMonthRange(project);
    }
  }

  // Cached week date ranges to avoid recomputing per project
  let weekDateRangesCache: { start: Date; end: Date }[] | null = null;
  let weekDateRangesCacheKey: string = '';

  function getWeekDateRanges(): { start: Date; end: Date }[] {
    const cacheKey = visibleWeeks.join(',');
    if (weekDateRangesCache && weekDateRangesCacheKey === cacheKey) {
      return weekDateRangesCache;
    }
    weekDateRangesCache = visibleWeeks.map((wk) => ({
      start: getWeekStart(wk),
      end: getWeekEnd(wk),
    }));
    weekDateRangesCacheKey = cacheKey;
    return weekDateRangesCache;
  }

  // Cached month date ranges
  let monthDateRangesCache: { start: Date; end: Date }[] | null = null;
  let monthDateRangesCacheKey: string = '';

  function getMonthDateRanges(): { start: Date; end: Date }[] {
    const cacheKey = visibleMonths.join(',');
    if (monthDateRangesCache && monthDateRangesCacheKey === cacheKey) {
      return monthDateRangesCache;
    }
    monthDateRangesCache = visibleMonths.map((mk) => {
      const [year, month] = mk.split('-').map(Number);
      return {
        start: new Date(Date.UTC(year, month - 1, 1)),
        end: new Date(Date.UTC(year, month, 0)),
      };
    });
    monthDateRangesCacheKey = cacheKey;
    return monthDateRangesCache;
  }

  function getProjectWeekRange(project: Project): { startIndex: number; endIndex: number } {
    // If project has explicit start/end dates, use those
    if (project.startDate && project.endDate) {
      const startDate = parseCalendarDate(project.startDate);
      const endDate = parseCalendarDate(project.endDate);
      const ranges = getWeekDateRanges();

      let startIndex = -1;
      let endIndex = -1;

      for (let i = 0; i < ranges.length; i++) {
        if (startDate <= ranges[i].end && endDate >= ranges[i].start) {
          if (startIndex === -1) startIndex = i;
          endIndex = i;
        }
      }

      return {
        startIndex: startIndex >= 0 ? startIndex : 0,
        endIndex: endIndex >= 0 ? endIndex : 0,
      };
    }

    // Fallback: use prebuilt period index from getAllProjects
    return getRangeFromPeriodIndex(project.id);
  }

  function getProjectMonthRange(project: Project): { startIndex: number; endIndex: number } {
    // If project has explicit start/end dates, use those
    if (project.startDate && project.endDate) {
      const startDate = parseCalendarDate(project.startDate);
      const endDate = parseCalendarDate(project.endDate);
      const ranges = getMonthDateRanges();

      let startIndex = -1;
      let endIndex = -1;

      for (let i = 0; i < ranges.length; i++) {
        if (startDate <= ranges[i].end && endDate >= ranges[i].start) {
          if (startIndex === -1) startIndex = i;
          endIndex = i;
        }
      }

      return {
        startIndex: startIndex >= 0 ? startIndex : 0,
        endIndex: endIndex >= 0 ? endIndex : 0,
      };
    }

    // Fallback: use prebuilt period index from getAllProjects
    return getRangeFromPeriodIndex(project.id);
  }

  // Use prebuilt index for O(1) fallback lookups
  function getRangeFromPeriodIndex(projectId: string): { startIndex: number; endIndex: number } {
    const indices = projectPeriodIndex?.get(projectId);
    if (indices && indices.length > 0) {
      return { startIndex: indices[0], endIndex: indices[indices.length - 1] };
    }
    return { startIndex: 0, endIndex: 0 };
  }

  // Assign rows to projects to avoid overlaps (greedy algorithm)
  // Uses Set for O(1) occupancy lookups instead of Array.includes O(n)
  function assignRows(projects: Project[]): GanttProject[] {
    const projectsWithRange = projects
      .map((p) => {
        const columnRange = getProjectColumnRange(p);
        return { ...p, startIndex: columnRange.startIndex, endIndex: columnRange.endIndex };
      })
      .sort((a, b) => {
        if (a.startIndex !== b.startIndex) return a.startIndex - b.startIndex;
        return b.endIndex - b.startIndex - (a.endIndex - a.startIndex);
      });

    const rowOccupancy: Set<number>[] = [];
    const result: GanttProject[] = [];

    for (const project of projectsWithRange) {
      let assignedRow = -1;

      for (let row = 0; row < rowOccupancy.length; row++) {
        const occupied = rowOccupancy[row];
        let fits = true;
        for (let w = project.startIndex; w <= project.endIndex; w++) {
          if (occupied.has(w)) {
            fits = false;
            break;
          }
        }

        if (fits) {
          assignedRow = row;
          break;
        }
      }

      if (assignedRow === -1) {
        assignedRow = rowOccupancy.length;
        rowOccupancy.push(new Set());
      }

      for (let w = project.startIndex; w <= project.endIndex; w++) {
        rowOccupancy[assignedRow].add(w);
      }

      result.push({
        ...project,
        ganttStartIndex: project.startIndex,
        ganttEndIndex: project.endIndex,
        ganttRow: assignedRow,
      });
    }

    return result;
  }

  // Derived: gantt-ready projects
  let ganttProjects = $derived.by(() => {
    const allProjects = getAllProjects();
    return assignRows(allProjects);
  });

  // Max rows for height calculation
  let maxRow = $derived(
    ganttProjects.length > 0 ? Math.max(...ganttProjects.map((p) => p.ganttRow)) + 1 : 1,
  );

  // Column width (pixels) - wider for months
  let COLUMN_WIDTH = $derived(isWeekScale ? 100 : 140);
  const ROW_HEIGHT = 32;
  const ROW_GAP = 4;

  // Scrollable container ref
  let scrollContainer: HTMLElement;

  // Auto-scroll to current column on mount
  onMount(() => {
    if (scrollContainer && currentColumnIndex >= 0) {
      const scrollTarget =
        currentColumnIndex * COLUMN_WIDTH - scrollContainer.clientWidth / 2 + COLUMN_WIDTH / 2;
      scrollContainer.scrollLeft = Math.max(0, scrollTarget);
    }
  });

  // Project click handler
  function handleProjectClick(project: GanttProject) {
    hideProjectTooltip(true);
    openProjectRail(project.id);
  }

  // Add project
  function handleAddProject(columnKey: string) {
    if (isWeekScale) {
      const weekStart = getWeekStart(columnKey);
      const targetDate = weekStart.toISOString().split('T')[0];
      const monthKey = weekStart.toISOString().slice(0, 7);
      openProjectModalForCreate({
        schedulingState: 'scheduled',
        monthKey,
        targetDate,
        status: 'planned',
      });
    } else {
      openProjectModalForCreate({
        schedulingState: 'scheduled',
        monthKey: columnKey,
        status: 'planned',
      });
    }
  }

  // Drag and drop
  let draggingProjectId = $state<string | null>(null);
  let dropTargetColumn = $state<string | null>(null);

  function handleDragStart(e: DragEvent, project: GanttProject) {
    hideProjectTooltip(true);
    draggingProjectId = project.id;
    draggedProject.set(project);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/project', String(project.id));
    }
  }

  function handleDragEnd() {
    draggingProjectId = null;
    draggedProject.set(null);
  }

  function handleDragOver(e: DragEvent, columnKey: string) {
    e.preventDefault();
    if ($draggedProject) {
      dropTargetColumn = columnKey;
    }
  }

  function handleDragLeave(e: DragEvent) {
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      dropTargetColumn = null;
    }
  }

  function handleDrop(e: DragEvent, columnKey: string) {
    e.preventDefault();
    dropTargetColumn = null;

    if (!$draggedProject) return;

    const projectId = e.dataTransfer?.getData('application/project');
    if (projectId) {
      if (isWeekScale) {
        requestProjectMove(projectId, $draggedProject, 'week', {
          weekKey: columnKey,
          status: 'planned',
        });
      } else {
        requestProjectMove(projectId, $draggedProject, 'month', {
          monthKey: columnKey,
          status: 'planned',
        });
      }
    }
  }

  // Get week state
  function getWeekState(weekKey: string): 'past' | 'current' | 'future' {
    if (isCurrentWeek(weekKey)) return 'current';
    if (weekKey < currentWeek) return 'past';
    return 'future';
  }

  // Get month state
  function getMonthState(monthKey: string): 'past' | 'current' | 'future' {
    if (isCurrentMonth(monthKey)) return 'current';
    if (monthKey < currentMonth) return 'past';
    return 'future';
  }
</script>

<div class="gantt-container">
  <!-- Scrollable area containing both header and content -->
  <div class="gantt-scroll-area" bind:this={scrollContainer}>
    <div class="gantt-canvas" style="width: {columns.length * COLUMN_WIDTH}px;">
      <!-- Column headers (sticky at top) -->
      <div class="column-headers">
        {#each columns as col, i (col.key)}
          <div
            class="column-header"
            class:past={col.state === 'past'}
            class:current={col.state === 'current'}
            class:future={col.state === 'future'}
            style="width: {COLUMN_WIDTH}px; left: {i * COLUMN_WIDTH}px;"
          >
            <span class="column-label">{col.label}</span>
            <span class="column-sublabel">{col.sublabel}</span>
          </div>
        {/each}
      </div>

      <!-- Main content area -->
      <div
        class="gantt-content"
        style="height: {Math.max(maxRow * (ROW_HEIGHT + ROW_GAP), 200)}px;"
      >
        <!-- Column backgrounds and drop zones -->
        {#each columns as col, i (col.key)}
          <div
            class="column-bg"
            class:past={col.state === 'past'}
            class:current={col.state === 'current'}
            class:future={col.state === 'future'}
            class:drop-target={dropTargetColumn === col.key}
            style="left: {i * COLUMN_WIDTH}px; width: {COLUMN_WIDTH}px;"
            role="group"
            ondragover={(e) => handleDragOver(e, col.key)}
            ondragleave={handleDragLeave}
            ondrop={(e) => handleDrop(e, col.key)}
            ondblclick={() => handleAddProject(col.key)}
          ></div>
        {/each}

        <!-- Today/current period line -->
        {#if currentColumnIndex >= 0}
          {@const todayOffset = currentColumnIndex * COLUMN_WIDTH + COLUMN_WIDTH / 2}
          <div class="today-line" style="left: {todayOffset}px;"></div>
        {/if}

        <!-- Project bars -->
        {#each ganttProjects as project (project.id)}
          {@const left = project.ganttStartIndex * COLUMN_WIDTH + 4}
          {@const width = (project.ganttEndIndex - project.ganttStartIndex + 1) * COLUMN_WIDTH - 8}
          {@const top = project.ganttRow * (ROW_HEIGHT + ROW_GAP)}

          <button
            type="button"
            class="gantt-bar {statusAccentColors[project.status]}"
            style="left: {left}px; top: {top}px; width: {width}px; height: {ROW_HEIGHT}px; {progressStyle(
              project,
            )}"
            data-progress-complete={progressComplete(project) || undefined}
            class:dragging={draggingProjectId === project.id}
            draggable={!$viewport.isMobile}
            ondragstart={(e) => handleDragStart(e, project)}
            ondragend={handleDragEnd}
            onclick={() => handleProjectClick(project)}
            onmouseenter={(e) => showProjectTooltip(project, e.currentTarget as HTMLElement)}
            onmouseleave={() => hideProjectTooltip()}
            title="{project.title} ({project.status})"
          >
            <span class="bar-category">{categoryIcons[project.category]}</span>
            {#if project.isPriority}
              <span class="bar-star">★</span>
            {/if}
            <span class="bar-title">{project.title}</span>
            {#if project.totalWeeks && project.totalWeeks > 1}
              <span class="bar-duration">{project.totalWeeks}w</span>
            {/if}
          </button>
        {/each}
      </div>
    </div>
  </div>

  <!-- Empty state -->
  {#if ganttProjects.length === 0}
    <div class="empty-state">
      <p>No projects in this time range</p>
      <p class="hint">Double-click a column to add a project</p>
    </div>
  {/if}
</div>

<style>
  .gantt-container {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #f9fafb;
    padding: 0.375rem;
    gap: 0;
    overflow: hidden;
    position: relative;
  }

  /* Scroll area */
  .gantt-scroll-area {
    flex: 1;
    overflow: auto;
    background: white;
    border-radius: 0.375rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .gantt-canvas {
    min-height: 100%;
    position: relative;
  }

  /* Column headers */
  .column-headers {
    display: flex;
    position: sticky;
    top: 0;
    z-index: 20;
    background: white;
    border-bottom: 2px solid #e5e7eb;
    height: 48px;
    min-height: 48px;
  }

  .column-header {
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0.375rem 0.25rem;
    background: #f9fafb;
    border-right: 1px solid #e5e7eb;
    transition: background 0.15s;
    height: 48px;
    box-sizing: border-box;
  }

  .column-header.past {
    background: #f3f4f6;
    opacity: 0.8;
  }

  .column-header.current {
    background: #dbeafe;
    border-bottom: 2px solid #3b82f6;
    margin-bottom: -2px;
  }

  .column-header.future {
    background: #fafbff;
  }

  .column-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #374151;
    white-space: nowrap;
  }

  .column-sublabel {
    font-size: 0.625rem;
    font-weight: 600;
    color: #9ca3af;
    background: #e5e7eb;
    padding: 0.0625rem 0.375rem;
    border-radius: 0.25rem;
    margin-top: 0.25rem;
  }

  .column-header.current .column-sublabel {
    color: #1d4ed8;
    background: #bfdbfe;
  }

  /* Gantt content */
  .gantt-content {
    position: relative;
    padding-top: 0.375rem;
    padding-bottom: 0.5rem;
  }

  /* Column backgrounds */
  .column-bg {
    position: absolute;
    top: 0;
    bottom: 0;
    border-right: 1px solid #f3f4f6;
    transition: background 0.15s;
  }

  .column-bg.past {
    background: #fafafa;
  }

  .column-bg.current {
    background: #eff6ff;
  }

  .column-bg.future {
    background: #fdfcff;
  }

  .column-bg.drop-target {
    background: #dbeafe !important;
    box-shadow: inset 0 0 0 2px #3b82f6;
  }

  /* Today line */
  .today-line {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #3b82f6;
    z-index: 5;
    pointer-events: none;
  }

  .today-line::before {
    content: '';
    position: absolute;
    top: -4px;
    left: -3px;
    width: 8px;
    height: 8px;
    background: #3b82f6;
    border-radius: 50%;
  }

  /* Gantt bars */
  .gantt-bar {
    position: absolute;
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0 0.625rem;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    background: #f9fafb;
    border: 1.5px solid var(--chip-border-color, #e5e7eb);
    cursor: grab;
    text-align: left;
    transition: all 0.15s;
    z-index: 10;
    overflow: hidden;
    white-space: nowrap;
  }

  .gantt-bar:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 20;
  }

  .gantt-bar.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }

  .bar-category {
    font-size: 0.75rem;
    flex-shrink: 0;
  }

  .bar-star {
    color: #facc15;
    font-size: 0.75rem;
    flex-shrink: 0;
  }

  .bar-title {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #374151;
    font-weight: 500;
  }

  .bar-duration {
    flex-shrink: 0;
    font-size: 0.625rem;
    font-weight: 600;
    font-family: ui-monospace, monospace;
    color: #1d4ed8;
    background: rgba(59, 130, 246, 0.15);
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
  }

  /* Empty state */
  .empty-state {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    color: #9ca3af;
    pointer-events: none;
  }

  .empty-state p {
    margin: 0;
    font-size: 0.875rem;
  }

  .empty-state .hint {
    font-size: 0.75rem;
    margin-top: 0.5rem;
    color: #d1d5db;
  }
</style>
